<template>
  <div>
    <div class="text-h5">Profile</div>
    <q-separator class="q-my-md" />
    <q-card flat bordered>
      <q-card-section class="flex justify-between">
        <div>
          <div class="text-h6">Profile</div>
          <div style="font-size: 12px">Update your profile information.</div>
        </div>
        <div class="q-my-auto">
          <q-btn
            v-if="isEmailPasswordUser"
            :loading="saveLoading"
            no-caps
            unelevated
            color="primary"
            :disable="!hasChanges"
            @click="updateUserData"
          >
            Save
          </q-btn>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <div class="q-gutter-x-xl" :class="{ row: $q.screen.gt.sm }">
          <div class="col q-gutter-y-md" :class="{ 'q-mt-md': $q.screen.lt.md }">
            <div class="row">
              <div class="col q-my-auto">
                <div class="row q-gutter-x-md">
                  <q-input
                    v-model="firstName"
                    label="First Name"
                    :readonly="!isEmailPasswordUser"
                    outlined
                    :bg-color="$q.dark.isActive ? 'grey-9' : 'white'"
                    class="col"
                  />
                  <q-input
                    v-model="lastName"
                    label="Last Name"
                    :readonly="!isEmailPasswordUser"
                    outlined
                    :bg-color="$q.dark.isActive ? 'grey-9' : 'white'"
                    class="col"
                  />
                </div>
              </div>
            </div>
            <div class="q-gutter-x-md" :class="{ row: $q.screen.gt.sm }">
              <q-input
                :class="{ 'q-my-md': $q.screen.lt.md }"
                label="Email"
                readonly
                :model-value="email"
                outlined
                :bg-color="$q.dark.isActive ? 'grey-9' : 'white'"
                class="col"
              >
                <template #append>
                  <q-chip
                    v-if="emailVerified"
                    color="green"
                    text-color="white"
                    icon-right="check"
                    class="q-my-auto"
                  >
                    Verified
                    <q-tooltip>{{ email }} is a verified email.</q-tooltip>
                  </q-chip>
                </template>
              </q-input>
              <div class="col" :class="{ 'q-my-md': $q.screen.lt.md }">
                <q-card
                  v-if="id?.startsWith('google-oauth2')"
                  flat
                  bordered
                  class="col q-px-md"
                  style="height: 56px"
                >
                  <div class="row q-gutter-x-md full-height">
                    <div style="width: 32px" class="q-my-auto">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 48 48"
                        style="display: block"
                      >
                        <path
                          fill="#EA4335"
                          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                        ></path>
                        <path
                          fill="#4285F4"
                          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                        ></path>
                        <path
                          fill="#FBBC05"
                          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                        ></path>
                        <path
                          fill="#34A853"
                          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                        ></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                      </svg>
                    </div>
                    <div class="col q-my-auto">
                      <div>Connected with Google</div>
                    </div>
                  </div>
                </q-card>
              </div>
            </div>
          </div>
          <div>
            <q-avatar size="96px" round class="bg-grey-4">
              <q-img v-if="picture" :src="picture" referrerpolicy="no-referrer" no-spinner />
              <q-icon v-else name="person" />
            </q-avatar>
          </div>
        </div>
      </q-card-section>
    </q-card>
    <div class="flex justify-between q-mt-md">
      <div v-if="updatedAt" class="q-my-auto">Last Updated: {{ updatedAt }}</div>
      <q-btn
        v-if="email && isEmailPasswordUser"
        no-caps
        outline
        :loading="saveLoading"
        @click="sendResetPasswordEmail"
      >
        <q-icon name="lock_reset" class="q-mr-sm" />
        Send Reset Password Email
      </q-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { formatDate } from '@/utils'
import { useUserStore } from '@/stores/user'

export default {
  name: 'SettingsProfileView',
  data() {
    return {
      saveLoading: false,
      id: null as string | null,
      firstName: null as string | null,
      lastName: null as string | null,
      email: null as string | null,
      emailVerified: false,
      picture: null as string | null,
      updatedAt: null as string | null,
      changingPassword: false
    }
  },
  computed: {
    isEmailPasswordUser() {
      return this.id?.startsWith('auth0')
    },
    hasChanges() {
      const user = this.$auth0.user.value
      return user?.given_name !== this.firstName || user?.family_name !== this.lastName
    }
  },
  mounted() {
    const user = this.$auth0.user.value
    this.id = user?.sub || null
    this.firstName = user?.given_name || null
    this.lastName = user?.family_name || null
    this.email = user?.email || null
    this.emailVerified = user?.email_verified || false
    this.picture = user?.picture || null
    this.updatedAt = formatDate(user?.updated_at || null)
  },
  methods: {
    sendResetPasswordEmail() {
      this.saveLoading = true
      if (this.email) {
        useUserStore()
          .requestPasswordReset(this.email)
          .then(() => {
            this.$q.notify({
              message: 'Reset password email sent.',
              color: 'positive',
              position: 'top'
            })
          })
          .catch(() => {
            this.$q.notify({
              message: 'Failed to send reset password email. We are already working on it.',
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.saveLoading = false
          })
      }
    },
    async updateUserData() {
      const payload: {
        given_name?: string
        family_name?: string
      } = {}
      const user = this.$auth0.user.value
      if (this.firstName && user?.given_name !== this.firstName) {
        payload.given_name = this.firstName
      }
      if (this.lastName && user?.family_name !== this.lastName) {
        payload.family_name = this.lastName
      }
      if (Object.keys(payload).length) {
        this.saveLoading = true
        await useUserStore()
          .updateUserData(payload)
          .then(() => {
            this.$auth0.getAccessTokenSilently({ cacheMode: 'off' }).then(() => {
              this.saveLoading = false
            })
          })
      }
    }
  }
}
</script>
